import { Box, Container, Typography, Grid, Paper, Stack, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { URLHandler } from "src/services/URLHandler";
import Label from "@mui/icons-material/Label";
import Category from "@mui/icons-material/Category";
import { useGetAllTagTypes, useGetAllTagValues } from "src/api/client-api/tag";
import { TAG_PAGINATION } from "src/constants";

export const TagOverview = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Récupération des données de base pour l'overview
    const {
        tagTypes = [],
        totalItems: totalTagTypes = 0,
        isLoading: isLoadingTagTypes,
    } = useGetAllTagTypes({ itemsPerPage: TAG_PAGINATION.TAG_OVERVIEW_ITEMS });

    const { data: tagValuesResponse, isLoading: isLoadingTagValues } = useGetAllTagValues({
        itemsPerPage: TAG_PAGINATION.TAG_OVERVIEW_ITEMS,
    });

    const tagValues = tagValuesResponse?.tagValues || [];
    const totalTagValues = tagValuesResponse?.totalItems || 0;

    // Entity tags are no longer tracked separately (legacy system removed)

    const stats = {
        totalTagTypes,
        totalTagValues,
        activeTagTypes: tagTypes.filter((tt) => tt.active).length,
        activeTagValues: tagValues.length, // All tag values are now considered active
    };

    const overviewCards = [
        {
            title: "Types de Tags",
            value: stats.totalTagTypes,
            subtitle: t("tags.overview.active_tag_types", "{{count}} actifs", { count: stats.activeTagTypes }),
            icon: <Category sx={{ fontSize: 48 }} />,
            onClick: () => navigate(URLHandler.settings.tags.tagTypes.list()),
            color: "#2563eb",
        },
        {
            title: "Valeurs de Tags",
            value: stats.totalTagValues,
            subtitle: t("tags.overview.active_tag_values", "{{count}} actives", { count: stats.activeTagValues }),
            icon: <Label sx={{ fontSize: 48 }} />,
            onClick: () => navigate(URLHandler.settings.tags.tagValues.list()),
            color: "#059669",
        },
    ];

    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                m: "0",
                p: "5rem !important",
            }}
        >
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
                <Typography component="h1" variant="h2">
                    {t("tags.titles.tag_management", "Gestion des Tags")}
                </Typography>
            </Stack>

            <Divider sx={{ mb: 4 }} />

            <Grid container spacing={3} sx={{ mb: 4 }}>
                {overviewCards.map((card, index) => (
                    <Grid item xs={12} md={4} key={index}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                cursor: "pointer",
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: 3,
                                },
                                border: `2px solid ${card.color}`,
                                borderRadius: 2,
                            }}
                            onClick={card.onClick}
                        >
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Box sx={{ color: card.color }}>{card.icon}</Box>
                                <Box>
                                    <Typography variant="h3" sx={{ color: card.color, fontWeight: "bold" }}>
                                        {card.value}
                                    </Typography>
                                    <Typography variant="h6" sx={{ color: "text.primary" }}>
                                        {card.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        {card.subtitle}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Typography variant="h5" sx={{ mb: 2 }}>
                {t("tags.overview.quick_overview", "Vue d'ensemble rapide")}
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t("tags.overview.tag_types_by_context", "Types de Tags par Contexte")}
                        </Typography>
                        <Stack spacing={1}>
                            {["global", "product", "ingredient", "organization", "cms", "supplier"].map((context) => {
                                const count = tagTypes.filter((tt) => tt.context === context).length;
                                return count > 0 ?
                                        <Box key={context} sx={{ display: "flex", justifyContent: "space-between" }}>
                                            <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                                                {context}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                {count}
                                            </Typography>
                                        </Box>
                                    :   null;
                            })}
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t("tags.overview.quick_actions", "Actions Rapides")}
                        </Typography>
                        <Stack spacing={2}>
                            <Box
                                sx={{
                                    p: 2,
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 1,
                                    cursor: "pointer",
                                    "&:hover": { backgroundColor: "#f5f5f5" },
                                }}
                                onClick={() => navigate(URLHandler.settings.tags.tagTypes.create())}
                            >
                                <Typography variant="body1">
                                    {t("tags.actions.create_new_tag_type", "Create new tag type")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t(
                                        "tags.descriptions.define_new_tag_type",
                                        "Define a new tag type with its properties",
                                    )}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 2,
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 1,
                                    cursor: "pointer",
                                    "&:hover": { backgroundColor: "#f5f5f5" },
                                }}
                                onClick={() => navigate(URLHandler.settings.tags.tagValues.create())}
                            >
                                <Typography variant="body1">
                                    {t("tags.actions.add_tag_value", "Add tag value")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t(
                                        "tags.descriptions.create_new_value_for_existing_type",
                                        "Créer une nouvelle valeur pour un type existant",
                                    )}
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};
